// Wrapper components used by PropertyDetail.scene.tsx routes.
// Each one reads :propertyId, determines whether it's a group or unit,
// then renders the relevant tab.

import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { EPropertyTypeId } from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Page } from "@rentiohq/web-shared/dist/components";
import React from "react";
import { useParams } from "react-router-dom";
import { SettlementInfo } from "../scenes/SettlementInfo/SettlementInfo";
import {
  useApportionmentKeysEnabled,
  useCostsEnabled,
} from "../useCostsEnabled";
import { ApportionmentKeysTab } from "./ApportionmentKeysTab";
import { CostsTab } from "./CostsTab";

function useGroupContext() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const { detail: property } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: propertyId ? +propertyId : undefined,
  });
  const isGroup = property?.typeId === EPropertyTypeId.Group;
  return { propertyId, property, isGroup };
}

export const ApportionmentKeysRoute: React.FC = () => {
  const { propertyId, isGroup } = useGroupContext();
  // Deep links follow the same rule that hides the tab: the office's costs
  // feature.
  const enabled = useApportionmentKeysEnabled();
  if (!enabled) return null;
  if (!propertyId) return null;
  if (!isGroup)
    return (
      <Page title={getLocalizedText("costs.keys.title")}>
        {getLocalizedText("costs.keys.group_only")}
      </Page>
    );
  return <ApportionmentKeysTab groupId={propertyId} />;
};

export const CostsRoute: React.FC = () => {
  const { propertyId, property, isGroup } = useGroupContext();
  // Deep links follow the same rule that hides the tab: the office's costs
  // feature.
  const enabled = useCostsEnabled();
  if (!enabled) return null;
  if (!propertyId) return null;
  return (
    <CostsTab
      scope={isGroup ? "group" : "unit"}
      groupId={isGroup ? propertyId : undefined}
      unitId={isGroup ? undefined : propertyId}
      isArchived={!!property?.archivedAt}
      propertyParentId={property?.parentId}
    />
  );
};

export const SettlementWizardRoute: React.FC = () => {
  // Deep links follow the same rule that hides the tab: the office's costs
  // feature.
  const enabled = useCostsEnabled();
  if (!enabled) return null;
  // SettlementInfo reads :propertyId + :settlementId from the URL and renders the Page/Card wizard.
  return <SettlementInfo />;
};
